import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { concatMap, switchMap, take, tap } from 'rxjs/operators';
import { filter, Subscription } from 'rxjs';

import { Webhook } from '@shared/webhook/models';
import { WebSocketEventsEnum } from '@shared/websocket/enums';
import { WebhookService } from '../../services';
import { AddEditWebhookComponent } from '../../modals';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { WebhookModalModel } from '../../models';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { SocketService } from '@tablesready/frontend-store';

@Component({
  selector: 'tablesready-webhooks',
  templateUrl: './webhooks.component.html',
})
export class WebhooksComponent implements OnInit, OnDestroy {
  @Input() disabled: boolean = true;
  icon = AppIcon;
  size = AppIconSize;
  webhooks: Webhook[] = [];
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly webhookService: WebhookService,
    private readonly modalController: FrontendModalService,
    private readonly socketService: SocketService,
  ) {
  }

  ngOnInit(): void {
    this.webhookService.getList()
      .pipe(tap(webhooks => this.webhooks = webhooks))
      .subscribe();
    const webhooksSubscription = this.socketService.getEventStream(WebSocketEventsEnum.webhooks)
      .pipe(
        switchMap(() => this.webhookService.getList()),
        tap(webhooks => this.webhooks = webhooks)
      )
      .subscribe();
    this.subscriptions.add(webhooksSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  addWebhook() {
    this.modalController.create<AddEditWebhookComponent, {
      data: WebhookModalModel;
      id?: string;
    }>(AddEditWebhookComponent)
      .pipe(
        take(1),
        filter(data => !!data),
        concatMap(({ data }) => this.webhookService.create(data)),
      )
      .subscribe();
  }

  editWebhook(webhook: Webhook) {
    const { _id, url, description, events} = webhook;
    this.modalController.create<AddEditWebhookComponent, {
      data: WebhookModalModel;
      id?: string;
    }>(AddEditWebhookComponent, {
      id: _id,
      webhook: {
        url,
        description,
        events,
      },
    })
      .pipe(
        take(1),
        filter(data => !!data),
        concatMap(({ data, id }) => this.webhookService.update(id, data)),
      )
      .subscribe();
  }

  deleteWebhook(id: string) {
    this.webhookService.delete(id)
      .subscribe();
  }
}
